import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const InventoryContext = createContext();

export const useInventory = () => {
  return useContext(InventoryContext);
};

export const InventoryProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, initialized: authInitialized } = useAuth();

  useEffect(() => {
    if (authInitialized && user) {
      fetchData();
    } else if (authInitialized && !user) {
      setProducts([]);
      setPallets([]);
      setInventoryHistory([]);
      setLoading(false);
    }
  }, [user, authInitialized]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`*, salesListings:sales_listings(*), pallet:pallets(*)`)
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;

      const transformedProducts = (productsData || []).map(p => ({
        ...p,
        localSku: p.local_sku,
        localPrice: p.local_price,
        // Ensure images is always an array. If p.images is null, fallback to p.image wrapped in an array.
        images: Array.isArray(p.images) && p.images.length > 0 
          ? p.images 
          : (p.image ? [p.image] : []),
        salesListings: (p.salesListings || []).map(sl => ({ ...sl, productId: sl.product_id }))
      }));
      setProducts(transformedProducts);

      const { data: palletsData, error: palletsError } = await supabase
        .from('pallets')
        .select('*')
        .order('created_at', { ascending: false });
      if (palletsError) throw palletsError;
      setPallets(palletsData || []);

      const { data: historyData, error: historyError } = await supabase
        .from('inventory_history')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(100);
      if (historyError) throw historyError;
      setInventoryHistory((historyData || []).map(h => ({
        ...h,
        productId: h.product_id,
        user: h.user_email,
        previousQuantity: h.previous_quantity,
        new_quantity: h.new_quantity
      })));
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (productData) => {
    try {
      const dbProduct = {
        manufacturer: productData.manufacturer,
        model: productData.model,
        name: productData.name,
        description: productData.description,
        upc: productData.upc,
        local_sku: productData.localSku,
        local_price: productData.localPrice,
        image: productData.images?.[0] || '', // Primary display image
        images: productData.images || [],     // All captured photos
        quantity: 0,
        pallet_id: productData.palletId || null
      };

      const { data, error } = await supabase
        .from('products')
        .insert(dbProduct)
        .select(`*, pallet:pallets(*)`)
        .single();

      if (error) throw error;
      const newProduct = {
        ...data,
        localSku: data.local_sku,
        localPrice: data.local_price,
        images: data.images || (data.image ? [data.image] : []),
        salesListings: []
      };
      setProducts(prev => [newProduct, ...prev]);
      toast.success('Product added successfully');
      return newProduct;
    } catch (error) {
      toast.error('Error adding product: ' + error.message);
      return null;
    }
  };

  const updateProduct = async (id, updates) => {
    try {
      const dbUpdates = { ...updates };
      if (updates.localSku) dbUpdates.local_sku = updates.localSku;
      if (updates.localPrice) dbUpdates.local_price = updates.localPrice;
      
      // Update both column and array for consistency
      if (updates.images) {
        dbUpdates.images = updates.images;
        dbUpdates.image = updates.images[0] || '';
      }
      
      delete dbUpdates.localSku;
      delete dbUpdates.localPrice;
      delete dbUpdates.salesListings;
      delete dbUpdates.pallet;

      const { error } = await supabase
        .from('products')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      toast.success('Product updated');
      return true;
    } catch (error) {
      toast.error('Error updating product');
      return false;
    }
  };

  const adjustInventory = async (productId, quantityChange, type, reason, userName) => {
    const product = products.find(p => p.id === productId);
    if (!product) return false;
    
    const newQuantity = Math.max(0, product.quantity + quantityChange);
    try {
      const { error: updateError } = await supabase
        .from('products')
        .update({ quantity: newQuantity })
        .eq('id', productId);
      if (updateError) throw updateError;

      const historyEntry = {
        product_id: productId,
        type,
        quantity: quantityChange,
        reason,
        user_email: userName,
        previous_quantity: product.quantity,
        new_quantity: newQuantity,
        timestamp: new Date().toISOString()
      };

      const { data: hData, error: hError } = await supabase
        .from('inventory_history')
        .insert(historyEntry)
        .select()
        .single();
      
      if (hError) throw hError;

      setProducts(prev => prev.map(p => p.id === productId ? { ...p, quantity: newQuantity } : p));
      setInventoryHistory(prev => [{
        ...hData,
        productId: hData.product_id,
        user: hData.user_email,
        previousQuantity: hData.previous_quantity,
        newQuantity: hData.new_quantity
      }, ...prev]);

      return true;
    } catch (error) {
      toast.error('Failed to update inventory');
      return false;
    }
  };

  const getLockedQuantity = (product) => {
    if (!product?.salesListings) return 0;
    return product.salesListings.filter(l => ['active', 'pending_shipping', 'waiting_for_pickup'].includes(l.status)).length;
  };

  const addSalesListing = async (productId, listing) => {
    const autoSalesId = `SL-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
    try {
      const { data, error } = await supabase
        .from('sales_listings')
        .insert({
          product_id: productId,
          platform: listing.platform,
          url: listing.url,
          price: listing.price,
          status: listing.status || 'active',
          grade: listing.grade,
          comments: listing.comments,
          sales_id: autoSalesId,
          images: listing.images || []
        })
        .select()
        .single();

      if (error) throw error;
      const newListing = { ...data, productId: data.product_id };
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, salesListings: [...(p.salesListings || []), newListing] } : p));
      toast.success('Listing added: ' + autoSalesId);
      return newListing;
    } catch (error) {
      toast.error('Error adding listing');
      return null;
    }
  };

  const updateSalesListing = async (productId, listingId, updates) => {
    try {
      const { error } = await supabase
        .from('sales_listings')
        .update(updates)
        .eq('id', listingId);
      if (error) throw error;
      setProducts(prev => prev.map(p => p.id === productId ? {
        ...p,
        salesListings: p.salesListings.map(l => l.id === listingId ? { ...l, ...updates } : l)
      } : p));
      toast.success('Listing updated');
    } catch (error) {
      toast.error('Error updating listing');
    }
  };

  const deleteSalesListing = async (productId, listingId) => {
    try {
      const { error } = await supabase.from('sales_listings').delete().eq('id', listingId);
      if (error) throw error;
      setProducts(prev => prev.map(p => p.id === productId ? {
        ...p,
        salesListings: p.salesListings.filter(l => l.id !== listingId)
      } : p));
      toast.success('Listing removed');
    } catch (error) {
      toast.error('Error deleting listing');
    }
  };

  const findProductByCode = (code, palletId = null) => {
    const norm = code.toLowerCase().trim();
    const matches = products.filter(p => 
      p.upc === code || p.localSku === code || p.localSku?.toLowerCase() === norm || 
      p.salesListings?.some(l => l.sales_id === code)
    );
    if (palletId) {
      const pMatch = matches.find(p => p.pallet_id === palletId);
      if (pMatch) return pMatch;
    }
    return matches[0] || null;
  };

  const value = {
    products, pallets, inventoryHistory, loading, error,
    addProduct, updateProduct, deleteProduct: async (id) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (!error) {
        setProducts(prev => prev.filter(p => p.id !== id));
        toast.success('Product deleted');
      }
    },
    adjustInventory, findProductByCode, addSalesListing, updateSalesListing, deleteSalesListing,
    getProductById: (id) => products.find(p => p.id === id),
    getLowStockProducts: (t = 10) => products.filter(p => p.quantity <= t),
    getTotalValue: () => products.reduce((t, p) => t + (p.quantity * p.localPrice), 0),
    getRecentActivity: (d = 7) => inventoryHistory,
    getLockedQuantity,
    addPallet: async (d) => {
      const { data, error } = await supabase.from('pallets').insert(d).select().single();
      if (!error) setPallets(prev => [data, ...prev]);
      return data;
    },
    updatePallet: async (id, u) => {
      const { error } = await supabase.from('pallets').update(u).eq('id', id);
      if (!error) setPallets(prev => prev.map(p => p.id === id ? { ...p, ...u } : p));
    },
    deletePallet: async (id) => {
      const { error } = await supabase.from('pallets').delete().eq('id', id);
      if (!error) setPallets(prev => prev.filter(p => p.id !== id));
    }
  };

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
};